import { z } from "zod";

import { timestampSchema } from "~/modules/ai-edit-tool/schemas";
import { jerboaBrowserInfoSchema } from "~/modules/jerboa/schemas";

export const errorSeveritySchema = z.enum(["info", "warning", "error", "fatal"]);
export type ErrorSeverity = z.infer<typeof errorSeveritySchema>;

export const errorContextSchema = z.enum(["main", "export_worker"]);

export const errorInfoCodeSchema = z.enum([
  "wasm_compile_failed",
  "wasm_runtime_panic",
  "webgpu_init_failed",
  "webgpu_device_lost",
  "segmentation_model_load_failed",
  "segmentation_inference_failed",
  "decoder_unsupported",
  "decoder_fatal",
  "encoder_error",
  "worker_spawn_failed",
  "project_load_failed",
  "react_uncaught",
  "oom",
  "unknown",
]);
export type ErrorInfoCode = z.infer<typeof errorInfoCodeSchema>;

export const errorInfoSubsystemSchema = z.enum([
  "preview",
  "export",
  "editor_session",
  "codec",
  "network",
  "segmentation",
  "react",
  "wasm_load",
]);
export type ErrorInfoSubsystem = z.infer<typeof errorInfoSubsystemSchema>;

export const errorInfoSchema = z.object({
  severity: errorSeveritySchema,
  code: errorInfoCodeSchema,
  subsystem: errorInfoSubsystemSchema,
  context: errorContextSchema,
  message: z.string(),
  stack: z.string().nullish(),
});

const jerboaClientErrorSummarySchema = z.object({
  id: z.string(),
  errorInfo: errorInfoSchema,
  fingerprint: z.string(),
  sessionId: z.string().nullish(),
  jobId: z.string().nullish(),
  videoId: z.string().nullish(),
  projectId: z.string().nullish(),
  captionsProjectId: z.string().nullish(),
  editType: z.enum(["captions", "ai-edit"]).nullish(),
  occurredAtMs: z.number(),
  appVersion: z.string().nullish(),
  jerboaVersion: z.string().nullish(),
  browserInfo: jerboaBrowserInfoSchema.passthrough().nullish(),
  createdAt: timestampSchema,
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
  // ENG-1217: server-side noise classification. `noise` rows are hidden
  // by default and only returned when the list is queried with
  // `includeNoise=true`; `noiseReason` explains which rule matched.
  noise: z.boolean().nullish(),
  noiseReason: z.string().nullish(),
});

export type JerboaClientErrorSummary = z.infer<typeof jerboaClientErrorSummarySchema>;

export const jerboaClientErrorsListResponseSchema = z.object({
  page: z.array(jerboaClientErrorSummarySchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
});

export const jerboaClientErrorDetailResponseSchema = z.object({
  result: jerboaClientErrorSummarySchema,
});

const clientErrorAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number(),
  fatalCount: z.number().default(0),
  errorCount: z.number().default(0),
  warningCount: z.number().default(0),
  severityCounts: z.record(z.string(), z.number()).nullish(),
  codeCounts: z.record(z.string(), z.number()).nullish(),
  subsystemCounts: z.record(z.string(), z.number()).nullish(),
  contextCounts: z.record(z.string(), z.number()).nullish(),
  uniqueFingerprintCount: z.number().default(0),
  uniqueSessionCount: z.number().default(0),
  captionsCount: z.number().default(0),
  aiEditCount: z.number().default(0),
  appVersions: z.array(z.string()).nullish(),
  appVersionCounts: z.record(z.string(), z.number()).nullish(),
  appPrimaryVersion: z.string().nullish(),
  appPrimaryVersionShare: z.number().nullish(),
  appVersionCoverage: z.number().nullish(),
  appVersionFirstSeenInBucket: z.record(z.string(), z.number()).nullish(),
  releaseMarkers: z.array(z.object({ version: z.string(), firstSeenAt: z.number() })).nullish(),
  appVersionFirstSeenMap: z.record(z.string(), z.number()).nullish(),
});

export type ClientErrorAnalysisBucket = z.infer<typeof clientErrorAnalysisBucketSchema>;

export const clientErrorAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(clientErrorAnalysisBucketSchema),
  }),
});
